import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SubTitle from '../common/SubTitle';
import { Box, TextField, Button, Typography, CircularProgress, Grid, RadioGroup, Radio, FormControlLabel, Select, MenuItem, FormControl, OutlinedInput, useMediaQuery, FormHelperText, Alert, Snackbar } from '@mui/material';
import { GoogleMap, LoadScript, Circle, Polygon } from '@react-google-maps/api';
import axios from 'axios';
import config from '../../config.json';

// API functions - keeping these unchanged
async function getSiteById(id) {
  try {
    const response = await axios.get(`${config.baseURL}/site/id?id=${id}`, {
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    console.log("Error in fetching site data: ", error);
    throw error;
  }
}

async function createSite(site) {
  try {
    const response = await axios.post(`${config.baseURL}/site`, site, {
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    console.log("Error in creating site: ", error);
    throw error;
  }
}

async function updateSite(site) {
  try {
    const response = await axios.put(`${config.baseURL}/site`, site, {
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    console.log("Error in updating site: ", error);
    throw error;
  }
}

async function assignVehiclesToSite(id, vehicleIds) {
  try {
    const response = await axios.put(`${config.baseURL}/site/vehicles`, { siteId: id, vehicleIds }, {
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    console.log("Error in assigning vehicles to site: ", error);
    throw error;
  }
}

async function getAllTheVehicles() {
  try {
    const response = await axios.get(`${config.baseURL}/vehicle/organization`, {
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    console.log("Error in fetching vehicles: ", error);
    throw error;
  }
}

export default function AddSiteMain() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const userStateString = localStorage.getItem('userState');
  const mapRef = useRef(null);

  // Media queries for responsive design
  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:960px)');

  // Parse the JSON string to get the object
  const userState = JSON.parse(userStateString);

  // Extract the organization ID
  const organizationId = userState?.user?.organizationId || '';
  const [site, setSite] = useState({
    siteId: '',
    name: '',
    description: '',
    siteManager: '',
    organizationId: organizationId,
    sitCoordinates: [
      { longitude: 0, latitude: 0 }
    ],
    radius: ''
  });

  // Validation states
  const [errors, setErrors] = useState({
    siteId: '',
    name: '',
    radius: '',
    coordinates: [],
    general: ''
  });

  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'error'
  });

  const [isUpdating, setIsUpdating] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [boundaryType, setBoundaryType] = useState('circular');
  const [mapCenter, setMapCenter] = useState({ lat: 13.025, lng: 77.628 });
  const [mapLoaded, setMapLoaded] = useState(false);
  const [touched, setTouched] = useState({});
  const [mapZoom, setMapZoom] = useState(10);

  // Map constants - adjust height for mobile
  const mapContainerStyle = {
    width: '100%',
    height: isMobile ? '300px' : '500px',
    borderRadius: '8px'
  };

  // Calculate appropriate zoom level based on radius
  const calculateZoomLevel = (radiusInKm) => {
    if (!radiusInKm) return 10; // Default zoom level

    // Formula to calculate appropriate zoom level based on radius
    // Smaller radius needs higher zoom level (closer)
    const radius = parseFloat(radiusInKm);

    if (radius <= 0.1) return 18;
    if (radius <= 0.25) return 17;
    if (radius <= 0.5) return 16;
    if (radius <= 1) return 15;
    if (radius <= 2) return 14;
    if (radius <= 5) return 13;
    if (radius <= 10) return 12;
    if (radius <= 20) return 11;
    if (radius <= 40) return 10;
    if (radius <= 80) return 9;
    if (radius <= 150) return 8;
    if (radius <= 300) return 7;
    return 6; // For very large radius
  };

  // Fetch site data if updating
  useEffect(() => {
    if (siteId) {
      setIsLoading(true);
      setIsUpdating(true);
      getSiteById(siteId)
        .then((data) => {
          setSite(data);
          // Set map center based on first coordinate
          if (data.sitCoordinates && data.sitCoordinates.length > 0) {
            setMapCenter({
              lat: parseFloat(data.sitCoordinates[0].latitude) || 0,
              lng: parseFloat(data.sitCoordinates[0].longitude) || 0
            });
          }
          // Set boundary type based on data
          setBoundaryType(data.radius ? 'circular' : 'custom');
          // Calculate zoom level based on radius if available
          if (data.radius) {
            setMapZoom(calculateZoomLevel(data.radius));
          }
          // If site has vehicles, set them as selected
          if (data.vehicles && data.vehicles.length > 0) {
            setSelectedVehicles(data.vehicles.map(v => v.id));
          }
        })
        .catch((err) => {
          console.log(err);
          setNotification({
            open: true,
            message: 'Failed to load site data: ' + (err.response?.data?.message || err.message),
            severity: 'error'
          });
        })
        .finally(() => setIsLoading(false));
    }

    // Fetch all vehicles for selection
    getAllTheVehicles()
      .then((data) => setVehicles(data))
      .catch((err) => {
        console.log(err);
        setNotification({
          open: true,
          message: 'Failed to load vehicles: ' + (err.response?.data?.message || err.message),
          severity: 'error'
        });
      });
  }, [siteId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSite((prevSite) => ({
      ...prevSite,
      [name]: value
    }));

    // If radius is changing, update the zoom level
    if (name === 'radius' && value) {
      setMapZoom(calculateZoomLevel(value));
    }

    // Mark field as touched
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCoordinatesChange = (index, key, value) => {
    const newCoordinates = [...site.sitCoordinates];
    newCoordinates[index][key] = value;

    // Mark this coordinate as touched
    const newTouched = { ...touched };
    newTouched[`coordinate_${index}_${key}`] = true;
    setTouched(newTouched);

    // Clear coordinate error
    if (errors.coordinates && errors.coordinates[index]) {
      const newCoordErrors = [...errors.coordinates];
      newCoordErrors[index] = '';
      setErrors(prev => ({
        ...prev,
        coordinates: newCoordErrors
      }));
    }

    // Update map center for first coordinate
    if (index === 0) {
      setMapCenter({
        lat: key === 'latitude' ? parseFloat(value) || 0 : parseFloat(newCoordinates[0].latitude) || 0,
        lng: key === 'longitude' ? parseFloat(value) || 0 : parseFloat(newCoordinates[0].longitude) || 0
      });
    }

    setSite((prevSite) => ({
      ...prevSite,
      sitCoordinates: newCoordinates
    }));
  };

  const handleBlur = (field) => {
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));
    validateField(field);
  };

  const handleCoordinateBlur = (index, key) => {
    const fieldName = `coordinate_${index}_${key}`;
    setTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));
    validateCoordinate(index, key);
  };

  const validateField = (field) => {
    let newErrors = { ...errors };

    switch (field) {
      case 'siteId':
        if (!site.siteId.trim()) {
          newErrors.siteId = 'Site ID is required';
        } else if (!/^[a-zA-Z0-9_-]+$/.test(site.siteId)) {
          newErrors.siteId = 'Site ID should only contain letters, numbers, hyphens, and underscores';
        } else {
          newErrors.siteId = '';
        }
        break;

      case 'name':
        if (!site.name.trim()) {
          newErrors.name = 'Site name is required';
        } else {
          newErrors.name = '';
        }
        break;

      case 'radius':
        if (boundaryType === 'circular') {
          if (!site.radius) {
            newErrors.radius = 'Radius is required for circular boundary';
          } else if (parseFloat(site.radius) <= 0) {
            newErrors.radius = 'Radius must be greater than 0';
          } else {
            newErrors.radius = '';
          }
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return !newErrors[field]; // Return true if valid
  };

  const validateCoordinate = (index, key) => {
    const coordinate = site.sitCoordinates[index];
    const value = coordinate[key];
    let newCoordErrors = [...(errors.coordinates || Array(site.sitCoordinates.length).fill(''))];

    // Validate longitude (-180 to 180)
    if (key === 'longitude') {
      if (value === '' || value === null) {
        newCoordErrors[index] = 'Longitude is required';
      } else if (isNaN(value) || parseFloat(value) < -180 || parseFloat(value) > 180) {
        newCoordErrors[index] = 'Longitude must be between -180 and 180';
      } else {
        newCoordErrors[index] = '';
      }
    }

    // Validate latitude (-90 to 90)
    if (key === 'latitude') {
      if (value === '' || value === null) {
        newCoordErrors[index] = 'Latitude is required';
      } else if (isNaN(value) || parseFloat(value) < -90 || parseFloat(value) > 90) {
        newCoordErrors[index] = 'Latitude must be between -90 and 90';
      } else {
        newCoordErrors[index] = '';
      }
    }

    setErrors(prev => ({
      ...prev,
      coordinates: newCoordErrors
    }));

    return !newCoordErrors[index]; // Return true if valid
  };

  const validatePolygon = () => {
    // Only needed for custom boundary with 3+ points
    if (boundaryType !== 'custom' || site.sitCoordinates.length < 3) {
      return true;
    }

    // Check if all points are unique
    const uniquePoints = new Set();
    let isValid = true;

    site.sitCoordinates.forEach((coord, index) => {
      const pointKey = `${coord.longitude},${coord.latitude}`;
      if (uniquePoints.has(pointKey)) {
        const newCoordErrors = [...(errors.coordinates || Array(site.sitCoordinates.length).fill(''))];
        newCoordErrors[index] = 'Duplicate coordinate point';
        setErrors(prev => ({
          ...prev,
          coordinates: newCoordErrors
        }));
        isValid = false;
      }
      uniquePoints.add(pointKey);
    });

    return isValid;
  };

  const validateForm = () => {
    // Validate all fields
    let isFormValid = true;
    let newErrors = { ...errors };

    // Site ID
    if (!site.siteId.trim()) {
      newErrors.siteId = 'Site ID is required';
      isFormValid = false;
    } else if (!/^[a-zA-Z0-9_-]+$/.test(site.siteId)) {
      newErrors.siteId = 'Site ID should only contain letters, numbers, hyphens, and underscores';
      isFormValid = false;
    } else {
      newErrors.siteId = '';
    }

    // Site Name
    if (!site.name.trim()) {
      newErrors.name = 'Site name is required';
      isFormValid = false;
    } else {
      newErrors.name = '';
    }

    // Coordinates validation
    const newCoordErrors = Array(site.sitCoordinates.length).fill('');

    site.sitCoordinates.forEach((coord, index) => {
      // Longitude check
      if (coord.longitude === '' || coord.longitude === null) {
        newCoordErrors[index] = 'Longitude is required';
        isFormValid = false;
      } else if (isNaN(coord.longitude) || parseFloat(coord.longitude) < -180 || parseFloat(coord.longitude) > 180) {
        newCoordErrors[index] = 'Longitude must be between -180 and 180';
        isFormValid = false;
      }

      // Latitude check
      if (coord.latitude === '' || coord.latitude === null) {
        newCoordErrors[index] = newCoordErrors[index] ?
          `${newCoordErrors[index]} and Latitude is required` :
          'Latitude is required';
        isFormValid = false;
      } else if (isNaN(coord.latitude) || parseFloat(coord.latitude) < -90 || parseFloat(coord.latitude) > 90) {
        newCoordErrors[index] = newCoordErrors[index] ?
          `${newCoordErrors[index]} and Latitude must be between -90 and 90` :
          'Latitude must be between -90 and 90';
        isFormValid = false;
      }
    });

    newErrors.coordinates = newCoordErrors;

    // Radius validation for circular boundary
    if (boundaryType === 'circular') {
      if (!site.radius) {
        newErrors.radius = 'Radius is required for circular boundary';
        isFormValid = false;
      } else if (parseFloat(site.radius) <= 0) {
        newErrors.radius = 'Radius must be greater than 0';
        isFormValid = false;
      } else {
        newErrors.radius = '';
      }
    }

    // Additional polygon validation for custom boundary
    if (boundaryType === 'custom' && site.sitCoordinates.length < 3) {
      newErrors.general = 'Custom boundary requires at least 3 coordinates';
      isFormValid = false;
    } else if (boundaryType === 'custom') {
      isFormValid = validatePolygon() && isFormValid;
    }

    setErrors(newErrors);
    return isFormValid;
  };

  const addCoordinate = () => {
    setSite((prevSite) => ({
      ...prevSite,
      sitCoordinates: [...prevSite.sitCoordinates, { longitude: 0, latitude: 0 }]
    }));

    // Add empty error slot for the new coordinate
    setErrors(prev => ({
      ...prev,
      coordinates: [...(prev.coordinates || []), '']
    }));
  };

  const removeCoordinate = (index) => {
    if (site.sitCoordinates.length > 1) {
      const newCoordinates = [...site.sitCoordinates];
      newCoordinates.splice(index, 1);
      setSite((prevSite) => ({
        ...prevSite,
        sitCoordinates: newCoordinates
      }));

      // Remove error for the deleted coordinate
      const newCoordErrors = [...(errors.coordinates || [])];
      newCoordErrors.splice(index, 1);
      setErrors(prev => ({
        ...prev,
        coordinates: newCoordErrors
      }));
    }
  };

  const handleBoundaryTypeChange = (event) => {
    setBoundaryType(event.target.value);
    // Reset radius if changing to custom boundary
    if (event.target.value === 'custom') {
      setSite((prevSite) => ({
        ...prevSite,
        radius: ''
      }));
      // Clear radius error
      setErrors(prev => ({
        ...prev,
        radius: ''
      }));
      // Reset zoom to default
      setMapZoom(10);
    }
  };

  const handleVehicleSelection = (event) => {
    setSelectedVehicles(event.target.value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    // Mark all fields as touched
    const allTouched = {
      siteId: true,
      name: true,
      radius: boundaryType === 'circular'
    };

    // Mark all coordinates as touched
    site.sitCoordinates.forEach((_, index) => {
      allTouched[`coordinate_${index}_longitude`] = true;
      allTouched[`coordinate_${index}_latitude`] = true;
    });

    setTouched(allTouched);

    // Validate all fields
    if (!validateForm()) {
      // Form is invalid, show error message
      setNotification({
        open: true,
        message: 'Please correct the errors before submitting',
        severity: 'error'
      });
      return;
    }

    setIsLoading(true);

    // Create a copy of the site object
    const siteData = {
      ...site,
      // Convert radius from string to float
      radius: site.radius ? parseFloat(site.radius) : null
    };

    const action = isUpdating ? updateSite : createSite;
    action(siteData)
      .then((res) => {
        if (selectedVehicles.length > 0) {
          return assignVehiclesToSite(res.id, selectedVehicles);
        }
        return res;
      })
      .then((res) => {
        console.log("Site saved successfully:", res);
        setNotification({
          open: true,
          message: `Site ${isUpdating ? 'updated' : 'created'} successfully`,
          severity: 'success'
        });
        // Navigate after a short delay to allow the user to see the success message
        setTimeout(() => navigate('/site'), 1500);
      })
      .catch((err) => {
        console.log("Error saving site:", err);
        setNotification({
          open: true,
          message: `Failed to ${isUpdating ? 'update' : 'create'} site: ${err.response?.data?.message || err.message}`,
          severity: 'error'
        });
      })
      .finally(() => setIsLoading(false));
  };

  const handleCancel = () => {
    navigate('/site'); // Redirect to site listing page
  };

  // Get polygon path for custom boundary
  const getPolygonPath = () => {
    return site.sitCoordinates.map(coord => ({
      lat: parseFloat(coord.latitude) || 0,
      lng: parseFloat(coord.longitude) || 0
    }));
  };

  const createVehicle = () => {
    navigate('/vehicle/add');
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({
      ...prev,
      open: false
    }));
  };

  const onMapLoad = (map) => {
    mapRef.current = map;
    setMapLoaded(true);
  };

  return (
    <>
      <SubTitle
        title="Sites"
        path={isUpdating ? "Sites > Update Site" : "Sites > Create Site"}
      />

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      <Box component="form" onSubmit={handleSubmit} sx={{
        margin: { xs: "0px 16px", sm: "0px 24px", md: "0px 40px" }
      }}>
        {/* General Error Display */}
        {errors.general && (
          <Alert severity="error" sx={{ mb: 2 }}>{errors.general}</Alert>
        )}

        {/* TOP ROW: Map and Boundary */}
        <Box sx={{
          minHeight: { xs: "auto", md: "556px" },
          borderRadius: "10px",
          padding: { xs: "16px", sm: "20px", md: "24px" },
          gap: "20px",
          background: "#FFFFFF",
          border: "1px solid #A5DCF3",
          boxShadow: "-2px 1px 5px 0px #00000040",
          mb: 3,
          overflow: "hidden"
        }}>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {/* Map Section */}
            <Grid item xs={12} md={8}>
              <Box sx={{
                bgcolor: 'white',
                borderRadius: '8px',
                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                height: '100%',
                overflow: 'hidden'
              }}>
                <Box sx={{ width: '100%', borderRadius: '8px', overflow: 'hidden' }}>
                  <LoadScript
                    googleMapsApiKey={config.googleMapsApiKey || ""}
                  >
                    <GoogleMap
                      mapContainerStyle={mapContainerStyle}
                      center={mapCenter}
                      zoom={mapZoom}
                      onLoad={onMapLoad}
                    >
                      {mapLoaded && boundaryType === 'circular' && site.radius && (
                        <Circle
                          center={mapCenter}
                          radius={parseFloat(site.radius) * 1000} // Convert km to meters
                          options={{
                            fillColor: '#20A8E0',
                            fillOpacity: 0.3,
                            strokeColor: '#20A8E0',
                            strokeOpacity: 0.8,
                            strokeWeight: 2,
                          }}
                        />
                      )}

                      {mapLoaded && boundaryType === 'custom' && site.sitCoordinates.length >= 3 && (
                        <Polygon
                          paths={getPolygonPath()}
                          options={{
                            fillColor: '#20A8E0',
                            fillOpacity: 0.3,
                            strokeColor: '#20A8E0',
                            strokeOpacity: 0.8,
                            strokeWeight: 2,
                          }}
                        />
                      )}
                    </GoogleMap>
                  </LoadScript>
                </Box>
              </Box>
            </Grid>

            {/* Boundary Section */}
            <Grid item xs={12} md={4}>
              <Box sx={{
                bgcolor: 'white',
                borderRadius: '8px',
                height: '100%',
                overflow: 'hidden'
              }}>
                <Box sx={{ p: { xs: 2, md: 3 } }}>
                  <Box sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "flex-start", sm: "center" },
                    minHeight: "44px",
                    borderRadius: "8px",
                    gap: { xs: "8px", md: "16px" },
                    padding: "8px 12px",
                    background: "#E8EAEE",
                    mb: 2
                  }}>
                    <Typography>Boundary Type:</Typography>
                    <RadioGroup
                      row
                      name="boundaryType"
                      value={boundaryType}
                      onChange={handleBoundaryTypeChange}
                    >
                      <FormControlLabel
                        sx={{ marginTop: { xs: "0", md: "-10px" } }}
                        value="circular"
                        control={<Radio />}
                        label="Circular"
                      />
                      <FormControlLabel
                        sx={{ marginTop: { xs: "0", md: "-10px" } }}
                        value="custom"
                        control={<Radio />}
                        label="Custom"
                      />
                    </RadioGroup>
                  </Box>

                  {boundaryType === 'circular' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          Central Coordinates <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        <TextField
                          fullWidth
                          required
                          sx={{
                            width: "100%",
                            height: "55px",
                            borderRadius: "16px",
                            mb: 1,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "16px",
                              border: touched[`coordinate_0_longitude`] && errors.coordinates?.[0] ? "2px solid #d32f2f" : "2px solid #95A0B3"
                            }
                          }}
                          type="number"
                          size="small"
                          placeholder="Longitude"
                          value={site.sitCoordinates[0]?.longitude || ''}
                          onChange={(e) => handleCoordinatesChange(0, 'longitude', e.target.value)}
                          onBlur={() => handleCoordinateBlur(0, 'longitude')}
                          error={touched[`coordinate_0_longitude`] && Boolean(errors.coordinates?.[0])}
                          inputProps={{ step: 'any', min: -180, max: 180 }}
                        />
                        {touched[`coordinate_0_longitude`] && errors.coordinates?.[0] && (
                          <FormHelperText error>{errors.coordinates[0]}</FormHelperText>
                        )}

                        <TextField
                          fullWidth
                          required
                          sx={{
                            width: "100%",
                            height: "55px",
                            borderRadius: "16px",
                            mb: 1,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "16px",
                              border: touched[`coordinate_0_latitude`] && errors.coordinates?.[0] ? "2px solid #d32f2f" : "2px solid #95A0B3"
                            }
                          }}
                          type="number"
                          size="small"
                          placeholder="Latitude"
                          value={site.sitCoordinates[0]?.latitude || ''}
                          onChange={(e) => handleCoordinatesChange(0, 'latitude', e.target.value)}
                          onBlur={() => handleCoordinateBlur(0, 'latitude')}
                          error={touched[`coordinate_0_latitude`] && Boolean(errors.coordinates?.[0])}
                          inputProps={{ step: 'any', min: -90, max: 90 }}
                        />
                        {touched[`coordinate_0_latitude`] && errors.coordinates?.[0] && (
                          <FormHelperText error>{errors.coordinates[0]}</FormHelperText>
                        )}
                      </Box>

                      <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          Radius (Kilometre) <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        <TextField
                          fullWidth
                          required
                          sx={{
                            width: "100%",
                            height: "55px",
                            borderRadius: "16px",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "16px",
                              border: touched.radius && errors.radius ? "2px solid #d32f2f" : "2px solid #95A0B3"
                            }
                          }}
                          type="number"
                          size="small"
                          name="radius"
                          placeholder="Type Radius"
                          value={site.radius}
                          onChange={handleInputChange}
                          onBlur={() => handleBlur('radius')}
                          error={touched.radius && Boolean(errors.radius)}
                          helperText={touched.radius && errors.radius}
                          inputProps={{ step: 'any', min: 0 }}
                        />
                      </Box>
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Boundary Coordinates <span style={{ color: 'red' }}>*</span>
                        {boundaryType === 'custom' && site.sitCoordinates.length < 3 && (
                          <Typography color="error" variant="caption" component="span" sx={{ ml: 1 }}>
                            (Minimum 3 points required)
                          </Typography>
                        )}
                      </Typography>

                      {site.sitCoordinates.map((coord, index) => (
                        <Box key={index} sx={{
                          display: 'flex',
                          flexDirection: { xs: isMobile ? 'column' : 'row' },
                          gap: 1,
                          mb: 2
                        }}>
                          <Box sx={{ width: '100%' }}>
                            <TextField
                              fullWidth
                              required
                              sx={{
                                mb: 1,
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "16px",
                                  border: touched[`coordinate_${index}_longitude`] && errors.coordinates?.[index] ? "2px solid #d32f2f" : "2px solid #95A0B3"
                                }
                              }}
                              type="number"
                              size="small"
                              placeholder="Longitude"
                              value={coord.longitude}
                              onChange={(e) => handleCoordinatesChange(index, 'longitude', e.target.value)}
                              onBlur={() => handleCoordinateBlur(index, 'longitude')}
                              error={touched[`coordinate_${index}_longitude`] && Boolean(errors.coordinates?.[index])}
                              inputProps={{ step: 'any', min: -180, max: 180 }}
                            />
                          </Box>

                          <Box sx={{ width: '100%' }}>
                            <TextField
                              fullWidth
                              required
                              sx={{
                                mb: 1,
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "16px",
                                  border: touched[`coordinate_${index}_latitude`] && errors.coordinates?.[index] ? "2px solid #d32f2f" : "2px solid #95A0B3"
                                }
                              }}
                              type="number"
                              size="small"
                              placeholder="Latitude"
                              value={coord.latitude}
                              onChange={(e) => handleCoordinatesChange(index, 'latitude', e.target.value)}
                              onBlur={() => handleCoordinateBlur(index, 'latitude')}
                              error={touched[`coordinate_${index}_latitude`] && Boolean(errors.coordinates?.[index])}
                              inputProps={{ step: 'any', min: -90, max: 90 }}
                            />
                          </Box>

                          {site.sitCoordinates.length > 3 && (
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() => removeCoordinate(index)}
                              sx={{
                                minWidth: '40px',
                                p: 0,
                                alignSelf: { xs: isMobile ? 'flex-end' : 'auto' }
                              }}
                            >
                              X
                            </Button>
                          )}
                        </Box>
                      ))}

                      {touched[`coordinate_${site.sitCoordinates.length - 1}_latitude`] &&
                        errors.coordinates && errors.coordinates.some(error => error) && (
                          <FormHelperText error>
                            Please check all coordinate inputs
                          </FormHelperText>
                        )}

                      {boundaryType === 'custom' && (
                        <Button
                          variant="outlined"
                          onClick={addCoordinate}
                          sx={{ mt: 1 }}
                        >
                          + Add Coordinate
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* BOTTOM ROW: Site Details and Vehicles */}
        <Box sx={{
          borderRadius: "10px",
          padding: { xs: "16px", sm: "20px", md: "24px 24px 32px 24px" },
          gap: { xs: "20px", md: "36px" },
          background: "#FFFFFF",
          border: "1px solid #A5DCF3",
          boxShadow: "-2px 2px 5px 0px #00000040",
          overflow: "hidden"
        }}>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {/* Site Details Section */}
            <Grid item xs={12} md={8}>
              <Box sx={{
                bgcolor: 'white',
                borderRadius: '8px',
                height: '100%',
                overflow: 'hidden'
              }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontFamily: 'Roboto',
                    fontWeight: 500,
                    fontSize: '24px',
                    lineHeight: '100%',
                    letterSpacing: '0%',
                    padding: "0px 20px",
                    color: "#183059"
                  }}
                >
                  Site Details
                </Typography>
                <Box sx={{ p: { xs: 2, md: 3 } }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          Site ID <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        <TextField
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "16px",
                              border: touched.siteId && errors.siteId ? "2px solid #d32f2f" : "2px solid #95A0B3"
                            }
                          }}
                          fullWidth
                          required
                          name="siteId"
                          value={site.siteId}
                          onChange={handleInputChange}
                          onBlur={() => handleBlur('siteId')}
                          error={touched.siteId && Boolean(errors.siteId)}
                          helperText={touched.siteId && errors.siteId}
                          disabled={isUpdating}
                          placeholder="Type Site ID"
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          Site Name <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        <TextField
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "16px",
                              border: touched.name && errors.name ? "2px solid #d32f2f" : "2px solid #95A0B3"
                            }
                          }}
                          fullWidth
                          required
                          name="name"
                          value={site.name}
                          onChange={handleInputChange}
                          onBlur={() => handleBlur('name')}
                          error={touched.name && Boolean(errors.name)}
                          helperText={touched.name && errors.name}
                          placeholder="Type Site Name"
                          variant="outlined"
                          size="small"
                        />
                      </Box>

                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          Site Manager
                        </Typography>
                        <TextField
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "16px",
                              border: "2px solid #95A0B3"
                            }
                          }}
                          fullWidth
                          name="siteManager"
                          value={site.siteManager}
                          onChange={handleInputChange}
                          placeholder="Type Site Manager"
                          variant="outlined"
                          size="small"
                        />
                      </Box>

                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ mt: 2, width: '100%' }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Site Description
                      </Typography>
                      <TextField
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "16px",
                            border: "2px solid #95A0B3"
                          }
                        }}
                        fullWidth
                        multiline
                        rows={4}
                        name="description"
                        value={site.description}
                        onChange={handleInputChange}
                        placeholder="Write Description"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Vehicles Section */}
            <Grid item xs={12} md={4}>
              <Box sx={{
                bgcolor: 'white',
                borderRadius: '8px',
                height: '100%',
                overflow: 'hidden'
              }}>
                <Box sx={{
                  bgcolor: '#E8EAEE',
                  minHeight: "67px",
                  gap: "16px",
                  padding: { xs: "10px", md: "14px 10px" },
                  background: "#E8EAEE",
                  boxShadow: "0px 2px 5px 0px #00000040",
                  borderBottom: '1px solid #E0E0E0',
                  display: 'flex',
                  flexDirection: { xs: isMobile ? 'column' : 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: isMobile ? 'flex-start' : 'center' }
                }}>
                  <Typography variant="subtitle1" fontWeight={600}>Vehicles on this Site</Typography>
                  <Button
                    variant="contained"
                    onClick={createVehicle}
                    sx={{
                      height: "39px",
                      borderRadius: "8px",
                      gap: "10px",
                      padding: "10px 20px",
                      background: "#F9E719",
                      border: "1px solid #414958",
                      color: "#1F242E",
                      '&:hover': { bgcolor: '#E9D800' },
                      mt: { xs: isMobile ? 1 : 0 }
                    }}
                  >
                    Create Vehicle
                  </Button>
                </Box>

                <Box sx={{ p: { xs: 2, md: 3 } }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Select Vehicles:
                  </Typography>

                  <FormControl fullWidth sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "16px",
                      border: "2px solid #95A0B3"
                    }
                  }}>
                    <Select
                      multiple
                      value={selectedVehicles}
                      onChange={handleVehicleSelection}
                      input={<OutlinedInput />}
                      renderValue={(selected) => {
                        if (selected.length === 0) {
                          return <em>No vehicles selected</em>;
                        }
                        return selected.map(id => {
                          const vehicle = vehicles.find(v => v.id === id);
                          return vehicle ? vehicle.name : '';
                        }).join(', ');
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 224,
                            width: 250,
                          },
                        },
                      }}
                      sx={{ height: vehicles.length > 0 ? 'auto' : '150px' }}
                    >
                      {vehicles.length > 0 ? (
                        vehicles.map((vehicle) => (
                          <MenuItem key={vehicle.id} value={vehicle.id}>
                            {vehicle.name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>No vehicles available</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Buttons Section */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'center', // Center the buttons
            gap: 2,
            mt: 3,
            flexDirection: { xs: isMobile ? 'column' : 'row' }
          }}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleCancel}
              disabled={isLoading}
              sx={{
                width: '122px',
                height: '55px',
                borderRadius: '8px',
                borderWidth: '1px',
                padding: '16px 24px',
                background: '#F2F2F3',
                border: '1px solid #414958',
                fontFamily: 'Roboto',
                fontWeight: 600,
                fontSize: '20px',
                lineHeight: '100%',
                textTransform: "none",
                letterSpacing: '0%',
                color: '#111214',
                '&:hover': { bgcolor: '#E0E0E0', borderColor: '#D0D0D0' },
                width: { xs: isMobile ? '100%' : '122px' }
              }}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              sx={{
                width: '125px',
                height: '55px',
                borderRadius: '8px',
                borderWidth: '1px',
                padding: '16px 24px',
                background: '#F9E719',
                border: '1px solid #414958',
                fontFamily: 'Roboto',
                fontWeight: 600,
                fontSize: '20px',
                lineHeight: '100%',
                letterSpacing: '0%',
                textTransform: "none",
                color: '#111214',
                '&:hover': { bgcolor: '#E0D618' },
                width: { xs: isMobile ? '100%' : '125px' }
              }}
            >
              {isLoading ? <CircularProgress size={24} /> : (isUpdating ? 'Update' : 'Save')}
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
}